import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../msalConfig";
import { TOKEN_REFRESH_SYNC_LOCAL_STORAGE_KEY } from "../Constant";
import {
  clearTokenMismatch,
  getAccessTokenFromLocal,
  isTokenExpired,
  notifyTokenRefreshAcrossTabs,
  setAccessTokenInLocal,
  withTokenRefreshLock,
} from "../utils/common";
import { setLoginInProgress, setLoginPromise } from "../utils/httpClient";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  getUserLogin,
  resetUserLogin,
  resetUserLoginError,
} from "../store/slices/UserLogoutSlice";
import { useAppDispatch } from "../store/hooks";

export const useRefreshToken = () => {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const isLogin = useSelector(
    (state: RootState) => state.UserLogoutSlice.isLogin,
  );
  const isInValidToken = isTokenExpired(getAccessTokenFromLocal());

  useEffect(() => {
    const handleTokenRefreshSync = (event: StorageEvent) => {
      if (
        event.key !== TOKEN_REFRESH_SYNC_LOCAL_STORAGE_KEY ||
        event.newValue === null
      ) {
        return;
      }

      // Another tab refreshed the shared token and completed the backend
      // login. Keep this tab authenticated without calling /login again.
      if (!isTokenExpired(getAccessTokenFromLocal())) {
        dispatch(resetUserLogin());
      }
    };

    window.addEventListener("storage", handleTokenRefreshSync);
    return () => window.removeEventListener("storage", handleTokenRefreshSync);
  }, [dispatch]);

  useEffect(() => {
    const refreshMSALToken = async () => {
      let resolveRefresh: (() => void) | undefined;
      const refreshPromise = new Promise<void>((resolve) => {
        resolveRefresh = resolve;
      });

      clearTokenMismatch();
      // Pause API requests in every tab while one tab refreshes the shared
      // token. Otherwise the other tabs send the expired token and trigger
      // the global token-mismatch loader.
      setLoginInProgress(true);
      setLoginPromise(refreshPromise);

      try {
        await withTokenRefreshLock(async () => {
          // A different tab may have completed the refresh while this tab was
          // waiting for the lock.
          if (!isTokenExpired(getAccessTokenFromLocal())) {
            dispatch(resetUserLogin());
            return;
          }

          const loginResponse = await instance.ssoSilent(loginRequest);
          const refreshedToken = loginResponse?.accessToken || "";

          if (!refreshedToken) {
            dispatch(resetUserLoginError());
            return;
          }

          setAccessTokenInLocal(refreshedToken);

          const loginApiResponse = await dispatch(getUserLogin());

          if (
            loginApiResponse.type === "UserLogoutSlice/getUserLogin/fulfilled"
          ) {
            clearTokenMismatch();
            notifyTokenRefreshAcrossTabs();
            dispatch(resetUserLogin());
          } else {
            dispatch(resetUserLoginError());
          }
        });
      } catch (err) {
        console.log("Login not successful", err);
        dispatch(resetUserLoginError());
      } finally {
        setLoginInProgress(false);
        resolveRefresh?.();
      }
    };

    if (isInValidToken && isLogin) {
      refreshMSALToken().catch((err) => {
        console.error("Token refresh failed:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInValidToken, isLogin]);

  return true;
};
